import { useState, useEffect, useRef, RefObject } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const SEND_OTP_URL = `https://kkmawmfkuffejnemowho.supabase.co/functions/v1/send-otp`;
const VERIFY_OTP_URL = `https://kkmawmfkuffejnemowho.supabase.co/functions/v1/verify-otp`;
const SUBMIT_GHL_URL = `https://kkmawmfkuffejnemowho.supabase.co/functions/v1/submit-to-ghl`;

interface StepOption {
  value: string;
  icon: string;
  label: string;
  sub?: string;
}

interface Step {
  id: string;
  question: string;
  subtitle: string;
  options: StepOption[];
}

const STEPS: Step[] = [
  {
    id: 'property_type',
    question: "What type of property is it?",
    subtitle: "This helps us recommend the right system size for your roof.",
    options: [
      { value: 'Detached House', icon: 'ri-home-2-line', label: 'Detached House' },
      { value: 'Semi Detached House', icon: 'ri-home-line', label: 'Semi Detached' },
      { value: 'Terraced House', icon: 'ri-community-line', label: 'Terraced House' },
      { value: 'Flat or Apartment', icon: 'ri-building-2-line', label: 'Flat / Apartment' },
      { value: 'Bungalow', icon: 'ri-home-gear-line', label: 'Bungalow' },
    ],
  },
  {
    id: 'ownership',
    question: "Are you the...?",
    subtitle: "We need to confirm your relationship to the property.",
    options: [
      { value: 'Owner', icon: 'ri-user-heart-line', label: 'Owner' },
      { value: 'Landlord', icon: 'ri-key-2-line', label: 'Landlord' },
      { value: 'Potential Buyer', icon: 'ri-search-eye-line', label: 'Potential Buyer' },
    ],
  },
  {
    id: 'system_size',
    question: "What system size are you thinking?",
    subtitle: "Based on your typical household energy usage — we can advise if unsure.",
    options: [
      { value: 'Small (4kWp)', icon: 'ri-sun-foggy-line', label: 'Small (4kWp)', sub: '8 Panels' },
      { value: 'Medium (5kWp)', icon: 'ri-sun-line', label: 'Medium (5kWp)', sub: '12 Panels' },
      { value: 'Large (6kWp)', icon: 'ri-sun-fill', label: 'Large (6kWp)', sub: '16 Panels' },
      { value: "I'm not sure", icon: 'ri-question-line', label: "I'm not sure", sub: "We'll help you decide" },
    ],
  },
  {
    id: 'timeline',
    question: "When are you looking to install?",
    subtitle: "This helps us plan your free survey and installation slot.",
    options: [
      { value: 'As soon as possible', icon: 'ri-flashlight-line', label: 'As soon as possible' },
      { value: 'Within a month', icon: 'ri-calendar-check-line', label: 'Within a month' },
      { value: 'Within a year', icon: 'ri-calendar-2-line', label: 'Within a year' },
      { value: "I'm flexible", icon: 'ri-time-line', label: "I'm flexible" },
    ],
  },
  {
    id: 'battery_storage',
    question: "Would you like to add battery storage?",
    subtitle: "Store excess solar energy for use at night or during grid outages.",
    options: [
      { value: 'Not sure', icon: 'ri-question-line', label: 'Not sure' },
      { value: 'Yes', icon: 'ri-battery-2-charge-line', label: 'Yes please!' },
      { value: 'No', icon: 'ri-close-circle-line', label: 'No thanks' },
    ],
  },
];

const STEP_LABELS = ['Property', 'Ownership', 'System', 'Timeline', 'Battery', 'Details', 'Verify'];

type Answers = Record<string, string>;

export default function QuoteWizard() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  // OTP state
  const [otpCode, setOtpCode] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [normalisedPhone, setNormalisedPhone] = useState('');

  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalSteps = STEPS.length + 2; // +2 for contact + OTP
  const isContactStep = step === STEPS.length;
  const isOtpStep = step === STEPS.length + 1;

  // Read URL params on mount (from HeroSection redirect)
  useEffect(() => {
    const serviceFromUrl = searchParams.get('service');
    const postcodeFromUrl = searchParams.get('postcode');
    
    if (serviceFromUrl) {
      setAnswers(prev => ({ ...prev, service: serviceFromUrl }));
    }
    if (postcodeFromUrl) {
      setAnswers(prev => ({ ...prev, postcode: postcodeFromUrl }));
    }
  }, [searchParams]);

  useEffect(() => {
    setSelected(null);
  }, [step]);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const goToStep = (nextStep: number, dir: 'forward' | 'back') => {
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setStep(nextStep);
      setAnimating(false);
    }, 220);
  };

  const handleSelect = (value: string) => {
    const stepId = STEPS[step].id;
    setSelected(value);
    setTimeout(() => {
      setAnswers((prev) => ({ ...prev, [stepId]: value }));
      goToStep(step + 1, 'forward');
    }, 320);
  };

  const handleBack = () => {
    if (isOtpStep) {
      setOtpCode('');
      setOtpError('');
      goToStep(step - 1, 'back');
    } else if (step > 0) {
      goToStep(step - 1, 'back');
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setFormError('Please enter your full name'); return; }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) { setFormError('Please enter a valid email address'); return; }
    if (!phone.trim()) { setFormError('Please enter your mobile number'); return; }
    // Basic UK mobile check — must be 07xxx or +447xxx
    const stripped = phone.replace(/[\s\-().]/g, '');
    const isUKMobile = /^(\+447|07)\d{9,10}$/.test(stripped);
    if (!isUKMobile) { setFormError('Please enter a valid UK mobile number (e.g. 07700 900 000)'); return; }
    setFormError('');
    setSubmitting(true);

    try {
      const res = await fetch(SEND_OTP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Failed to send verification code. Please try again.');
        return;
      }
      setNormalisedPhone(data.phone);
      setOtpCode('');
      setOtpError('');
      startCooldown();
      goToStep(step + 1, 'forward');
    } catch {
      setFormError('Network error — please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    setOtpError('');
    try {
      const res = await fetch(SEND_OTP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        setOtpError(data.error || 'Failed to resend code.');
        return;
      }
      setNormalisedPhone(data.phone);
      setOtpCode('');
      startCooldown();
    } catch {
      setOtpError('Something went wrong. Please try again.');
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode.trim() || otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit code from your SMS.');
      return;
    }
    setOtpError('');
    setOtpVerifying(true);

    try {
      const verifyRes = await fetch(VERIFY_OTP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalisedPhone || phone, code: otpCode }),
      });
      const verifyData = await verifyRes.json();

      if (!verifyRes.ok) {
        setOtpError(verifyData.error || 'Verification failed. Please try again.');
        return;
      }

      // OTP verified — push to GHL first (primary), then backup form
      const ghlPayload = {
        name,
        email,
        phone: normalisedPhone || phone,
        source: 'Solar Quote Wizard',
        contact_type: 'Lead',
        tags: ['solar-quote', 'website-lead'],
        property_type: answers.property_type,
        ownership: answers.ownership,
        system_size: answers.system_size,
        timeline: answers.timeline,
        battery_storage: answers.battery_storage,
        service: answers.service,
        postcode: answers.postcode,
      };

      const ghlRes = await fetch(SUBMIT_GHL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ghlPayload),
      });

      const ghlData = await ghlRes.json().catch(() => ({}));

      if (!ghlRes.ok) {
        const msg = ghlData?.error || ghlData?.message || `CRM error ${ghlRes.status}`;
        setOtpError(`Submission failed: ${msg}. Please try again or call us on 0330 010 1234.`);
        return;
      }

      // Backup form (fire-and-forget — non-blocking)
      const formBody = new URLSearchParams({ name, email, phone, ...answers });
      fetch('https://readdy.ai/api/form/d74e0dqvu4krcbn3b470', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      }).catch(() => {});

      setSubmitted(true);
    } catch {
      setOtpError('Network error — please check your connection and try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const progressPct = (step / totalSteps) * 100;

  if (submitted) {
    return <SuccessScreen name={name} />;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(160deg, #0d3d22 0%, #145A32 60%, #0d3d22 100%)' }}>

      {/* Step indicator bar */}
      <div className="w-full border-b px-4 py-4" style={{ background: 'rgba(0,0,0,0.25)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between w-full">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-full border-2 transition-all duration-300 text-xs font-black"
                    style={{
                      background: i < step ? '#7AC943' : i === step ? '#7AC943' : 'rgba(255,255,255,0.08)',
                      borderColor: i <= step ? '#7AC943' : 'rgba(255,255,255,0.15)',
                      color: i <= step ? '#0d3d22' : 'rgba(255,255,255,0.35)',
                    }}
                  >
                    {i < step
                      ? <i className="ri-check-line text-xs" />
                      : <span>{i + 1}</span>
                    }
                  </div>
                  <span
                    className="text-[10px] font-semibold leading-tight hidden sm:block"
                    style={{ color: i === step ? '#7AC943' : i < step ? 'rgba(122,201,67,0.6)' : 'rgba(255,255,255,0.3)' }}
                  >
                    {label}
                  </span>
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <div
                    className="flex-1 h-0.5 mx-1 transition-all duration-500"
                    style={{ background: i < step ? '#7AC943' : 'rgba(255,255,255,0.1)' }}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-3 w-full rounded-full h-1.5 overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%`, background: '#7AC943' }}
            />
          </div>

          <div className="flex items-center justify-between mt-2">
            <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Step {step + 1} of {totalSteps}
            </span>
            {step > 0 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-xs font-bold cursor-pointer whitespace-nowrap transition-opacity hover:opacity-100 opacity-70"
                style={{ color: '#7AC943' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-s-line" />
                </div>
                Back
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 md:py-16">
        <div
          className={`w-full max-w-2xl transition-all duration-220 ${
            animating
              ? direction === 'forward'
                ? 'opacity-0 -translate-x-6'
                : 'opacity-0 translate-x-6'
              : 'opacity-100 translate-x-0'
          }`}
          style={{ transitionDuration: '220ms' }}
        >
          {!isContactStep && !isOtpStep && (
            <QuestionStep step={STEPS[step]} selected={selected} onSelect={handleSelect} />
          )}
          {isContactStep && (
            <ContactStep
              answers={answers}
              name={name} setName={setName}
              email={email} setEmail={setEmail}
              phone={phone} setPhone={setPhone}
              submitting={submitting}
              error={formError}
              onSubmit={handleContactSubmit}
            />
          )}
          {isOtpStep && (
            <OTPStep
              phone={phone}
              otpCode={otpCode}
              setOtpCode={setOtpCode}
              verifying={otpVerifying}
              error={otpError}
              resendCooldown={resendCooldown}
              onVerify={handleVerifyOTP}
              onResend={handleResendOTP}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pb-6 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
        <div className="flex items-center justify-center gap-3 mb-1">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-shield-check-line text-xs" style={{ color: '#7AC943' }} />
            </div>
            <span>MCS Certified</span>
          </div>
          <span>·</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-lock-line text-xs" style={{ color: '#7AC943' }} />
            </div>
            <span>No obligation quote</span>
          </div>
          <span>·</span>
          <Link to="/" className="transition-colors cursor-pointer hover:opacity-100" style={{ color: '#7AC943' }}>
            Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}

function QuestionStep({ step, selected, onSelect }: {
  step: Step;
  selected: string | null;
  onSelect: (v: string) => void;
}) {
  const cols = step.options.length <= 3 ? step.options.length : 2;

  return (
    <div>
      <div className="text-center mb-8 md:mb-10">
        <div
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full mb-3"
          style={{ background: 'rgba(122,201,67,0.2)', color: '#7AC943' }}
        >
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-sun-line text-xs" />
          </div>
          Free Solar Quote
        </div>
        <h2 className="font-heading font-black text-2xl md:text-3xl leading-tight mb-2" style={{ color: '#ffffff' }}>
          {step.question}
        </h2>
        <p className="text-sm md:text-base max-w-md mx-auto" style={{ color: 'rgba(255,255,255,0.65)' }}>
          {step.subtitle}
        </p>
      </div>

      <div
        className="grid gap-3 md:gap-4"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {step.options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            className="group flex flex-col items-center justify-center gap-3 p-5 md:p-6 rounded-2xl border-2 cursor-pointer transition-all duration-200 text-center"
            style={{
              background: selected === opt.value ? 'rgba(122,201,67,0.15)' : 'rgba(0,0,0,0.2)',
              borderColor: selected === opt.value ? '#7AC943' : 'rgba(255,255,255,0.1)',
              transform: selected === opt.value ? 'scale(0.98)' : 'scale(1)',
            }}
            onMouseEnter={(e) => {
              if (selected !== opt.value) {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(122,201,67,0.5)';
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(122,201,67,0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (selected !== opt.value) {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.1)';
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.2)';
              }
            }}
          >
            <div
              className="w-14 h-14 flex items-center justify-center rounded-full transition-all duration-200"
              style={{
                background: selected === opt.value ? 'rgba(122,201,67,0.25)' : 'rgba(122,201,67,0.08)',
              }}
            >
              <i
                className={`${opt.icon} text-2xl transition-colors duration-200`}
                style={{ color: selected === opt.value ? '#7AC943' : 'rgba(255,255,255,0.6)' }}
              />
            </div>
            <div>
              <div
                className="font-bold text-sm md:text-base transition-colors duration-200"
                style={{ color: selected === opt.value ? '#7AC943' : '#ffffff' }}
              >
                {opt.label}
              </div>
              {opt.sub && (
                <div
                  className="text-xs mt-0.5 transition-colors duration-200"
                  style={{ color: selected === opt.value ? 'rgba(122,201,67,0.8)' : 'rgba(255,255,255,0.45)' }}
                >
                  {opt.sub}
                </div>
              )}
            </div>
            {selected === opt.value && (
              <div
                className="w-5 h-5 flex items-center justify-center rounded-full"
                style={{ background: '#7AC943' }}
              >
                <i className="ri-check-line text-xs" style={{ color: '#0d3d22' }} />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function ContactStep({
  answers, name, setName, email, setEmail, phone, setPhone,
  submitting, error, onSubmit,
}: {
  answers: Answers;
  name: string; setName: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  submitting: boolean;
  error: string;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const summaryItems = [
    { icon: 'ri-home-2-line', label: answers.property_type },
    { icon: 'ri-user-heart-line', label: answers.ownership },
    { icon: 'ri-sun-line', label: answers.system_size },
    { icon: 'ri-calendar-check-line', label: answers.timeline },
    { icon: 'ri-battery-2-charge-line', label: `Battery: ${answers.battery_storage}` },
  ];

  return (
    <div>
      <div className="text-center mb-8">
        <div
          className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4"
          style={{ background: 'rgba(122,201,67,0.2)' }}
        >
          <i className="ri-checkbox-circle-line text-4xl" style={{ color: '#7AC943' }} />
        </div>
        <h2 className="font-heading font-black text-2xl md:text-3xl mb-2" style={{ color: '#ffffff' }}>
          Fantastic! Your solar options are now available
        </h2>
        <p className="text-sm md:text-base max-w-md mx-auto" style={{ color: 'rgba(255,255,255,0.65)' }}>
          Enter your details below — we&apos;ll verify your mobile number then send your personalised fixed-price quote.
        </p>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {summaryItems.map((item) => item.label && (
          <div
            key={item.label}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border"
            style={{ background: 'rgba(122,201,67,0.12)', borderColor: 'rgba(122,201,67,0.3)', color: '#7AC943' }}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={`${item.icon} text-xs`} />
            </div>
            {item.label}
          </div>
        ))}
      </div>

      {/* Contact form */}
      <div
        className="rounded-2xl border p-6 md:p-8 max-w-lg mx-auto"
        style={{ background: 'rgba(0,0,0,0.25)', borderColor: 'rgba(122,201,67,0.2)' }}
      >
        <form data-readdy-form onSubmit={onSubmit} className="flex flex-col gap-4">
          <input type="hidden" name="property_type" value={answers.property_type || ''} />
          <input type="hidden" name="ownership" value={answers.ownership || ''} />
          <input type="hidden" name="system_size" value={answers.system_size || ''} />
          <input type="hidden" name="timeline" value={answers.timeline || ''} />
          <input type="hidden" name="battery_storage" value={answers.battery_storage || ''} />

          <div>
            <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Full Name</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-user-line text-sm" style={{ color: 'rgba(255,255,255,0.35)' }} />
              </div>
              <input
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="w-full rounded-xl pl-9 pr-4 py-3 text-sm border-2 outline-none transition-all"
                style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(122,201,67,0.2)', color: '#ffffff' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#7AC943'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(122,201,67,0.2)'; }}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Email Address</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-mail-line text-sm" style={{ color: 'rgba(255,255,255,0.35)' }} />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
                className="w-full rounded-xl pl-9 pr-4 py-3 text-sm border-2 outline-none transition-all"
                style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(122,201,67,0.2)', color: '#ffffff' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#7AC943'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(122,201,67,0.2)'; }}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Mobile Number
              <span className="ml-1.5 font-normal" style={{ color: 'rgba(122,201,67,0.7)' }}>— we&apos;ll text you a verification code</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-smartphone-line text-sm" style={{ color: 'rgba(255,255,255,0.35)' }} />
              </div>
              <input
                type="tel"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07700 900 000"
                className="w-full rounded-xl pl-9 pr-4 py-3 text-sm border-2 outline-none transition-all"
                style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(122,201,67,0.2)', color: '#ffffff' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#7AC943'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(122,201,67,0.2)'; }}
              />
            </div>
          </div>

          {error && (
            <p
              className="text-xs rounded-lg px-3 py-2 border"
              style={{ color: '#ff6b6b', background: 'rgba(255,107,107,0.1)', borderColor: 'rgba(255,107,107,0.2)' }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full font-black py-4 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 mt-1 hover:opacity-90 flex items-center justify-center gap-2"
            style={{ background: '#7AC943', color: '#0d3d22' }}
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Sending verification code...
              </>
            ) : (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-message-2-line" />
                </div>
                Send Verification Code →
              </>
            )}
          </button>

          <p className="text-center text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.35)' }}>
            No obligation · No spam · Quote within 1 working day
          </p>
        </form>
      </div>
    </div>
  );
}

function OTPStep({
  phone, otpCode, setOtpCode, verifying, error, resendCooldown, onVerify, onResend,
}: {
  phone: string;
  otpCode: string;
  setOtpCode: (v: string) => void;
  verifying: boolean;
  error: string;
  resendCooldown: number;
  onVerify: () => void;
  onResend: () => void;
}) {
  const maskedPhone = phone.replace(/(\d{3})\d{4}(\d{3,4})$/, '$1 **** $2');
  const inputRef = useRef<HTMLInputElement>(null);

  // On mobile, focus + scroll the input into view without the keyboard pushing the button off screen
  useEffect(() => {
    const t = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 400);
    return () => clearTimeout(t);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onVerify();
  };

  return (
    <div className="max-w-md mx-auto w-full">
      <div className="text-center mb-8">
        <div
          className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-5"
          style={{ background: 'rgba(122,201,67,0.15)', border: '2px solid rgba(122,201,67,0.3)' }}
        >
          <i className="ri-message-3-line text-4xl" style={{ color: '#7AC943' }} />
        </div>
        <h2 className="font-heading font-black text-2xl md:text-3xl mb-2" style={{ color: '#ffffff' }}>
          Check your phone
        </h2>
        <p className="text-sm md:text-base" style={{ color: 'rgba(255,255,255,0.65)' }}>
          We&apos;ve sent a 6-digit code to
        </p>
        <p className="text-sm font-black mt-1" style={{ color: '#7AC943' }}>
          {maskedPhone}
        </p>
      </div>

      <div
        className="rounded-2xl border p-6 md:p-8"
        style={{ background: 'rgba(0,0,0,0.25)', borderColor: 'rgba(122,201,67,0.2)' }}
      >
        <div className="flex flex-col gap-5">
          <div>
            <label className="block text-xs font-bold mb-2 text-center" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Enter your 6-digit verification code
            </label>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={handleKeyDown}
              placeholder="• • • • • •"
              className="w-full rounded-xl px-4 py-4 text-center text-2xl font-black border-2 outline-none transition-all tracking-[0.4em]"
              style={{
                background: 'rgba(255,255,255,0.06)',
                borderColor: error ? 'rgba(255,107,107,0.5)' : otpCode.length === 6 ? '#7AC943' : 'rgba(122,201,67,0.25)',
                color: '#ffffff',
                letterSpacing: '0.4em',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#7AC943'; }}
              onBlur={(e) => {
                if (error) {
                  e.currentTarget.style.borderColor = 'rgba(255,107,107,0.5)';
                } else if (otpCode.length === 6) {
                  e.currentTarget.style.borderColor = '#7AC943';
                } else {
                  e.currentTarget.style.borderColor = 'rgba(122,201,67,0.25)';
                }
              }}
            />
          </div>

          {error && (
            <div
              className="flex items-start gap-2 text-xs rounded-lg px-3 py-2.5 border"
              style={{ color: '#ff6b6b', background: 'rgba(255,107,107,0.1)', borderColor: 'rgba(255,107,107,0.2)' }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line" />
              </div>
              {error}
            </div>
          )}

          <button
            onClick={onVerify}
            disabled={verifying || otpCode.length !== 6}
            className="w-full font-black py-4 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 hover:opacity-90 flex items-center justify-center gap-2"
            style={{ background: '#7AC943', color: '#0d3d22' }}
          >
            {verifying ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-line" />
                </div>
                Verify &amp; Submit Quote →
              </>
            )}
          </button>

          <div className="text-center">
            <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Didn&apos;t receive a code?
            </p>
            <button
              onClick={onResend}
              disabled={resendCooldown > 0}
              className="text-xs font-bold cursor-pointer whitespace-nowrap transition-opacity disabled:cursor-default"
              style={{
                color: resendCooldown > 0 ? 'rgba(255,255,255,0.3)' : '#7AC943',
              }}
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend code'
              }
            </button>
          </div>

          <div
            className="flex items-center gap-2 text-[11px] rounded-lg px-3 py-2.5"
            style={{ background: 'rgba(122,201,67,0.08)', color: 'rgba(255,255,255,0.45)' }}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
              <i className="ri-lock-line text-xs" style={{ color: '#7AC943' }} />
            </div>
            Your number is only used for quote verification — never shared or spammed.
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessScreen({ name }: { name: string }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 text-center"
      style={{ background: 'linear-gradient(160deg, #0d3d22 0%, #145A32 60%, #0d3d22 100%)' }}
    >
      <div
        className="w-20 h-20 flex items-center justify-center rounded-full mb-6"
        style={{ background: 'rgba(122,201,67,0.2)' }}
      >
        <i className="ri-checkbox-circle-fill text-5xl" style={{ color: '#7AC943' }} />
      </div>
      <h1 className="font-heading font-black text-3xl md:text-4xl mb-3" style={{ color: '#ffffff' }}>
        You&apos;re all set, {name.split(' ')[0]}!
      </h1>
      <p className="text-base max-w-sm mx-auto mb-8" style={{ color: 'rgba(255,255,255,0.65)' }}>
        We&apos;ve received your solar quote request and will be in touch within one working day with your personalised fixed-price options.
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Link
          to="/"
          className="font-black px-8 py-3.5 rounded-full text-sm cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity"
          style={{ background: '#7AC943', color: '#0d3d22' }}
        >
          Back to home
        </Link>
        <a
          href="tel:03300101234"
          className="flex items-center gap-2 border-2 font-black px-6 py-3.5 rounded-full text-sm cursor-pointer whitespace-nowrap transition-all hover:opacity-80"
          style={{ borderColor: '#7AC943', color: '#7AC943' }}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-line" />
          </div>
          Call us now
        </a>
      </div>
    </div>
  );
}